/* eslint-disable react/jsx-indent */
import { forOwn, isEmpty, noop, uniqBy } from 'lodash';
import Logger from '@sm360/logger-sentry';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { ConfigsService, LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import {
    Account,
    AccountService,
    AnalyticsService,
    VIRTUAL_PAGEVIEWS as PAGES,
    ProfileService,
    REDIRECTIONS,
} from '@sm360/widget-commons-general';
import { FIELD_LENGTH, InputText, ValidationService, InputColorRadioContainer } from '@sm360/widget-commons-forms';
import { Vehicle, ORGANIZATIONS } from '@sm360/widget-commons-vehicle';
import { MessageBox } from '@sm360/widget-commons-basic-components';
import { InputNumber, Card, CardSection } from '@sm360/ui-library';

import CBBAutoComplete from 'trade-in/components/CBBAutoComplete/CBBAutoComplete.jsx';

import TradeInVehicleMapperService from 'trade-in/services/TradeInVehicleMapperService/TradeInVehicleMapperService';
import TradeInService from 'trade-in/services/TradeInService/TradeInService.js';
import ShowroomCBBService from 'trade-in/services/ShowroomCBBService/ShowroomCBBService.js';

import 'trade-in/components/InstantEstimateWrapper/Steps/VehicleInfo/VehicleInfo.scss';
import NewTradeInContext from '../../../../NewTradeInContext';
import ActionButton from '../../Components/ActionButton/ActionButton.jsx';

const fieldValidations = {
    odometer: ValidationService.validateOdometer,
    vin: ValidationService.optional(ValidationService.validateVin),
    year: ValidationService.validateYear,
};

class VehicleInfo extends Component {
    constructor(props) {
        super(props);

        const { tradeInVehicle } = props;

        if (tradeInVehicle && tradeInVehicle.estimation) {
            // set update estimation.amountWasUpdated = true otherwise it will create a new lead
            tradeInVehicle.estimation.amountWasUpdated = true;
        }

        this.state = {
            formValidation: {
                make: false,
                model: false,
                year: false,
                exteriorColor: false,
                odometer: false,
                trim: false,
                vin: true,
            },
            hasBeenSubmitted: false,
            autoCompleteValue: '',
            tradeInVehicle,
            tradeInId: null,
            tradeInLeadId: null,
            account: props.account,
            displaySubmitError: false,
            displayCheckInfoMessage: false,
            estimation: null,
            profile: {},
            isLoggedIn: false,
            displayNotFoundError: false,
            displayLabel: false,
            displayAPISubmitError: undefined,
            fetching: false,
        };

        this.fillAutoCompleteValueFromTradeInVehicle = this.fillAutoCompleteValueFromTradeInVehicle.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleTradeInChange = this.handleTradeInChange.bind(this);
        this.isFieldValid = this.isFieldValid.bind(this);
        this.onAutoCompleteSelect = this.onAutoCompleteSelect.bind(this);
        this.sendVehicleBasicInfo = this.sendVehicleBasicInfo.bind(this);
        this.validateVehicleInformationForm = this.validateVehicleInformationForm.bind(this);
        this.handleEdit = this.handleEdit.bind(this);
        this.handleColorRadioChange = this.handleColorRadioChange.bind(this);
        this.handleMileageChange = this.handleMileageChange.bind(this);

        this.logger = new Logger(this.constructor.displayName, ConfigsService.getLoggerContext());

        this.cbbAutoCompleteRef = null;
        this.sendingTradeIn = false;
    }

    componentDidMount() {
        if (AccountService.isAuthenticated() && this.props.autofillTradeInVehicleFromAccount) {
            this.getAccountTradeIn();
            ProfileService.getLoggedAccountProfile().then((profile) => {
                this.setState({ isLoggedIn: true, profile });
            });
        }
        this.fillAutoCompleteValueFromTradeInVehicle();
        if (!this.props.isOfficialTradeIn) {
            AnalyticsService.virtualPageView(PAGES.ESTIMATED_TRADEIN_VEHICLE());
        } else {
            AnalyticsService.virtualPageView(PAGES.OFFICIAL_TRADEIN_VEHICLE_FORM());
        }
    }

    componentWillReceiveProps(newProps) {
        if (newProps.tradeInVehicle !== this.state.tradeInVehicle) {
            this.fillAutoCompleteValueFromTradeInVehicle(newProps.tradeInVehicle);
        }
    }

    onAutoCompleteSelect(autoCompleteValue, selectedValue) {
        let tradeInVehicle = null;
        const { odometer } = this.state.tradeInVehicle;
        if (selectedValue) {
            // Reset exteriorColor validation
            this.setState({
                formValidation: { ...this.state.formValidation, exteriorColor: false },
            });
            tradeInVehicle = {
                ...selectedValue,
                exteriorColor: undefined,
                odometer: odometer ? String(odometer) : '',
            };
        } else {
            tradeInVehicle = {
                exteriorColor: this.state.tradeInVehicle.exteriorColor,
                odometer: String(odometer),
            };
        }

        if (autoCompleteValue) {
            this.setState({ displayLabel: true });
        }

        this.setState({ autoCompleteValue, tradeInVehicle, estimation: null }, () => {
            this.props.onTradeInVehicleUpdated(tradeInVehicle);
        });
    }

    onCantFindVehicleClick() {
        const windowProxy = WindowProxy.get();
        const locale = LocaleService.getSimpleLocale();
        const organizationId = ConfigsService.getOrganizationId();
        const isShowroomV25Enabled = ConfigsService.isShowroomV25Enabled();

        // TODO : `Dilawri OrgId` condition to be removed when deploy to all dealers
        const isDilawri = organizationId === ORGANIZATIONS.DILAWRI;

        if (windowProxy) {
            if (isDilawri) {
                windowProxy.location.href = REDIRECTIONS[locale].tradeInAppraisalV25Dilawri;
            } else {
                windowProxy.location.href = isShowroomV25Enabled
                    ? REDIRECTIONS[locale].tradeInAppraisalV25
                    : REDIRECTIONS[locale].tradeInAppraisal;
            }
        }
    }

    getModelTrimStyle(vehicleId, year, make, model, trim) {
        // vehicleInfo: string - combination of vehicle year, make, model and trim
        const vehicleInfo = `${year} ${make} ${model} ${trim}`;
        // Extract model trim style from vehicle id
        return vehicleId.replace(new RegExp(`.*${vehicleInfo}`), '').trim();
    }

    getAccountTradeIn() {
        TradeInService.fetchTradeIn().then((tradeIn) => {
            const {
                estimation,
                id,
                leadId,
                vehicleId,
                make,
                model,
                trim,
                year,
                kilometers,
                exteriorColor,
                vin,
            } = tradeIn;

            const modelTrimStyle = this.getModelTrimStyle(vehicleId, year, make, model, trim);
            const vehicle = {
                id: vehicleId,
                make,
                model,
                modelTrim: trim,
                modelTrimStyle: [modelTrimStyle],
                year,
                odometer: kilometers,
                bodyColour: {
                    colorCode: exteriorColor,
                },
                vin,
            };

            const tradeInVehicle = TradeInVehicleMapperService.vehicleToTradeInVehicle({ ...vehicle });

            this.setState(
                (oldState) => ({ ...oldState, tradeInVehicle, tradeInId: id, tradeInLeadId: leadId, estimation }),
                () => this.fillAutoCompleteValueFromTradeInVehicle(tradeInVehicle)
            );
        });
    }

    getCBBAutoComplete() {
        return (
            <>
                {!this.state.displayLabel && (
                    // Display autocomplete text box with Can't find vehicle link
                    <div className="trade-in-form--autocomplete" key="vehicle-form">
                        <CBBAutoComplete
                            value={this.state.autoCompleteValue}
                            onValueSelected={this.onAutoCompleteSelect}
                            dataTestPrefix="trade-in.vehicle-form"
                            ref={(element) => {
                                this.cbbAutoCompleteRef = element;
                            }}
                            displayOnWrapper
                        />
                        <a onClick={this.onCantFindVehicleClick} className="trade-in-form--autocomplete-link" href="#">
                            {LocaleService.t('TRADE_IN.CANT_FIND_VEHICLE')}
                        </a>
                    </div>
                )}
                {this.state.displayLabel && (
                    // Display label with Edit button
                    <div className="trade-in-form--autocomplete-label">
                        <label>{this.state.tradeInVehicle && this.state.tradeInVehicle.value}</label>
                        <br />
                        {!this.context.fieldsLocked && (
                            <a href="#" onClick={this.handleEdit} className="trade-in-form--autocomplete-link">
                                {LocaleService.t('TRADE_IN.ESTIMATE.MODIFY')}
                            </a>
                        )}
                    </div>
                )}
            </>
        );
    }

    getVehicleDetailsFields(useCBB, isOfficial) {
        return useCBB && !isOfficial ? (
            this.getCBBAutoComplete()
        ) : (
            <div key="vehicle-basic-form">
                <InputText
                    label={LocaleService.t('TRADE_IN.YEAR')}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.YEAR')}
                    name="year"
                    type="number"
                    value={this.state.tradeInVehicle.year}
                    classNames={['styled-placeholder quarter']}
                    isValid={this.state.formValidation.year}
                    isRequired
                    valueChange={this.handleTradeInChange}
                    hasBeenSubmit={this.state.hasBeenSubmitted}
                    dataTest="trade-in.vehicle-form.year"
                    maxLength={FIELD_LENGTH.YEAR_MAX}
                />
                <InputText
                    label={LocaleService.t('TRADE_IN.MAKE')}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.MAKE')}
                    name="make"
                    type="text"
                    value={this.state.tradeInVehicle.make}
                    classNames={['styled-placeholder triple-quarter']}
                    isValid={this.state.formValidation.make}
                    isRequired
                    valueChange={this.handleTradeInChange}
                    hasBeenSubmit={this.state.hasBeenSubmitted}
                    dataTest="trade-in.vehicle-form.make"
                    maxLength={FIELD_LENGTH.MAKE_MAX}
                />
                <InputText
                    label={LocaleService.t('TRADE_IN.MODEL')}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.MODEL')}
                    name="model"
                    type="text"
                    value={this.state.tradeInVehicle.model}
                    classNames={['styled-placeholder half']}
                    isValid={this.state.formValidation.model}
                    isRequired
                    valueChange={this.handleTradeInChange}
                    hasBeenSubmit={this.state.hasBeenSubmitted}
                    dataTest="trade-in.vehicle-form.model"
                    maxLength={FIELD_LENGTH.MODEL_MAX}
                />
                <InputText
                    label={LocaleService.t('TRADE_IN.TRIM')}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.TRIM')}
                    name="trim"
                    type="text"
                    value={this.state.tradeInVehicle.trim}
                    classNames={['styled-placeholder half']}
                    isValid={this.state.formValidation.trim}
                    isRequired
                    valueChange={this.handleTradeInChange}
                    hasBeenSubmit={this.state.hasBeenSubmitted}
                    dataTest="trade-in.vehicle-form.trim"
                    maxLength={FIELD_LENGTH.TRIM_MAX}
                />
            </div>
        );
    }

    getVehicleBasicInfoForm() {
        const exteriorColors = this.state.tradeInVehicle?.exteriorColors || [];
        const otherColor = {
            description: 'Other',
            htmlColour: 'linear-gradient(to right, red,orange,yellow,green,blue,indigo,violet)',
        };
        if (exteriorColors) {
            exteriorColors.push(otherColor);
        }
        const vehicleColorMileageFields =
            exteriorColors && !isEmpty(this.state.autoCompleteValue)
                ? [
                      <div className="radio-color">
                          <p className="sr-text">{LocaleService.t('TRADE_IN.EXTERIOR_COLOR')}</p>
                          <InputColorRadioContainer
                              options={uniqBy(exteriorColors, 'description')}
                              onChange={(description, colorCode) => this.handleColorRadioChange(description, colorCode)}
                              isValid={this.state.formValidation.exteriorColor}
                              hasBeenSubmit={this.state.hasBeenSubmitted}
                              classNames={['radio-color-container']}
                              value={this.state?.tradeInVehicle?.exteriorColor?.colorCode}
                              errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.EXTERIOR_COLOR')}
                              disabled={this.context.fieldsLocked}
                          />
                      </div>,
                      <div className="clearfix">
                          <p className="sr-text trade-in-form--odometer">
                              {LocaleService.t('TRADE_IN.ODOMETER_READING')}
                          </p>
                          <InputNumber
                              id="odometer"
                              name="odometer"
                              label="KM"
                              customMessage={LocaleService.t('TRADE_IN.ODOMETER.ERROR', {
                                  min: 0,
                                  max: 185000,
                              })}
                              min={0}
                              max={185000}
                              value={this.state.tradeInVehicle.odometer}
                              classNames={['styled-placeholder half']}
                              locale={LocaleService.getSimpleLocale()}
                              required
                              floatLabel
                              onChange={(value) => this.handleMileageChange(value, 'odometer')}
                              disabled={this.context.fieldsLocked}
                          />
                      </div>,
                  ]
                : [];
        const vehicleFormFields = [
            this.getVehicleDetailsFields(this.props.useCBB, this.props.isOfficialTradeIn),
            ...vehicleColorMileageFields,
        ];

        return vehicleFormFields;
    }

    static contextType = NewTradeInContext;

    handleColorRadioChange(description, colorCode) {
        const selectedExteriorColor = { description, colorCode };
        this.setState({
            tradeInVehicle: {
                ...this.state.tradeInVehicle,
                exteriorColor: selectedExteriorColor,
            },
            formValidation: {
                ...this.state.formValidation,
                exteriorColor: this.isFieldValid(colorCode, description),
            },
        });
    }

    handleEdit() {
        this.setState({
            displayLabel: false,
        });
    }

    handleMileageChange(value, name) {
        const { tradeInVehicle } = this.state;
        const formattedValue = value ? parseInt(value, 10) : '';
        tradeInVehicle[name] = ['year', 'odometer'].includes(name) ? formattedValue : value;

        this.setState({
            tradeInVehicle,
            formValidation: {
                ...this.state.formValidation,
                [name]: this.isFieldValid(value, name),
            },
        });
    }

    fillAutoCompleteValueFromTradeInVehicle(updatedVehicle) {
        const tradeInVehicle = updatedVehicle || this.state.tradeInVehicle;

        if (!isEmpty(tradeInVehicle)) {
            this.setState(
                (oldState) => {
                    return {
                        ...oldState,
                        tradeInVehicle,
                        // TODO: Find a way to use either tradeInVehicle.id or tradeInVehicle.value
                        autoCompleteValue:
                            !tradeInVehicle.id || tradeInVehicle.id === 0 ? tradeInVehicle.value : tradeInVehicle.value,
                        displayCheckInfoMessage: true,
                        displayLabel: this.props.displayLabel,
                    };
                },
                () => {
                    this.validateVehicleInformationForm(tradeInVehicle);
                    this.props.onTradeInVehicleUpdated(tradeInVehicle);
                }
            );
        }
    }

    validateVehicleInformationForm(vehicle) {
        const formValidation = { ...this.state.formValidation };
        forOwn(formValidation, (isValid, key) => {
            if (key !== 'identityGroup') {
                formValidation[key] = this.isFieldValid(vehicle[key], key);
            }
        });
        this.setState({ formValidation });
    }

    isFieldValid(value, name) {
        return fieldValidations[name] ? fieldValidations[name](value) : ValidationService.validateIsNotEmpty(value);
    }

    handleSubmit(event) {
        event.preventDefault();
    }

    handleTradeInChange(value, name) {
        const { tradeInVehicle } = this.state;
        tradeInVehicle[name] = ['year', 'odometer'].includes(name) ? parseInt(value, 10) : value;

        this.setState(
            {
                tradeInVehicle,
                formValidation: {
                    ...this.state.formValidation,
                    [name]: this.isFieldValid(value, name),
                },
                // Set estimation to null, once trade in vehicle information changed
                // Setting estimation to null to get new estimation
                estimation: null,
            },
            this.props.onTradeInVehicleUpdated(tradeInVehicle)
        );
    }

    sendVehicleBasicInfo() {
        return Promise.resolve(ValidationService.validateForm(this.state.formValidation)).then((isFormValid) => {
            if (!isFormValid) {
                this.setState({
                    hasBeenSubmitted: true,
                });
                return false;
            }

            this.setState({ displaySubmitError: false, fetching: true });
            const { tradeInVehicle } = this.state;

            // If fields are locked, no need to update token
            if (this.context.fieldsLocked) return this.props.onNextStep();

            // Get the trade in vehicle token and valuation token
            // Pass this valuation token along with trade vehicle info to the next screen
            // TODO: Need validation to call this API if comes from previous page and page info not changed??
            // TODO: No need to call this API if screen is locked after step5
            ShowroomCBBService.updateTradeVehicleToken(
                tradeInVehicle.tradeVehicleToken,
                tradeInVehicle?.odometer,
                tradeInVehicle?.exteriorColor?.description
            )
                .then((result) => {
                    tradeInVehicle.tradeVehicleToken = result.tradeVehicleToken;
                    this.props.onTradeInVehicleUpdated(tradeInVehicle);
                    return this.props.onFormSubmit(tradeInVehicle);
                })
                .catch((err) => {
                    this.setState({
                        displayAPISubmitError: err,
                        fetching: false,
                    });
                });
            // DON'T pass to second screen if CBB API returns an error
            return false;
        });
    }

    render() {
        return (
            <div className="vehicle-info-form">
                {this.state.displayAPISubmitError && (
                    // Display an error box if CBB API returns an error on valuation token generation
                    <div className="API-error-msg">
                        <Card color="alert" outline>
                            <CardSection>
                                <icon className="icon-Warning" />
                                <p>{this.state.displayAPISubmitError}</p>
                            </CardSection>
                        </Card>
                    </div>
                )}
                <h5>{LocaleService.t('TRADE_IN.TITLE_NEW_TRADEIN')}</h5>
                <div className="vehicle-info-form-body">
                    <form className="trade-in-form" onSubmit={this.handleSubmit}>
                        <div className="trade-in-cta">
                            {this.state.displaySubmitError && (
                                <MessageBox
                                    level="error"
                                    message={
                                        this.state.displayNotFoundError
                                            ? LocaleService.t('TRADE_IN.VEHICLE_NOT_FOUND')
                                            : LocaleService.t('TRADE_IN.ERROR')
                                    }
                                />
                            )}
                        </div>
                        <section className="clearfix vehicle-info-form-body--vehicle-details">
                            <p className="sr-text">{LocaleService.t('TRADE_IN.YOUR_VEHICLE')}</p>

                            {this.getVehicleBasicInfoForm()}
                        </section>

                        {this.state.autoCompleteValue && (
                            <section
                                className="clearfix vehicle-buttons-container"
                                data-test="trade-in.vehicle-form.submit"
                            >
                                <div className="vehicle-buttons">
                                    <ActionButton
                                        next
                                        onClick={this.sendVehicleBasicInfo}
                                        loading={this.state.fetching}
                                    >
                                        {LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_OPTIONS')}
                                    </ActionButton>
                                </div>
                            </section>
                        )}
                    </form>
                </div>
            </div>
        );
    }
}

VehicleInfo.defaultProps = {
    autofillTradeInVehicleFromAccount: true,
    updateAccount: noop,
    quote: null,
    account: new Account(),
};

VehicleInfo.propTypes = {
    autofillTradeInVehicleFromAccount: PropTypes.bool,
    onFormSubmit: PropTypes.func.isRequired,
    onTradeInVehicleUpdated: PropTypes.func.isRequired,
    useCBB: PropTypes.bool.isRequired,
    isOfficialTradeIn: PropTypes.bool.isRequired,
    tradeInVehicle: PropTypes.instanceOf(Vehicle.TradeInVehicleModel).isRequired,
    account: PropTypes.instanceOf(Account),
};

VehicleInfo.displayName = 'VehicleInfo';

export default VehicleInfo;
